import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Truck, Store, Clock, MapPin, ChevronRight } from "lucide-react";
import { OrderMode } from "@/types";
import { useApp } from "@/App";
import apiConfig from "@/configs/apiConfig";

interface DeliveryTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderMode: OrderMode;
  onOrderModeChange: (mode: OrderMode) => void;
}

export function DeliveryTypeModal({
  isOpen,
  onClose,
  orderMode,
  onOrderModeChange,
}: DeliveryTypeModalProps) {
  const [selectedType, setSelectedType] = useState<"pickup" | "delivery">(
    orderMode.type
  );
  const { restaurant } = useApp();
  const deliveryFee = apiConfig.DELIVERY_FEE;
  const handleConfirm = () => {
    onOrderModeChange({
      ...orderMode,
      type: selectedType,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md mx-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 font-display mb-2">
            Choose Order Type
          </DialogTitle>
          <p className="text-gray-600 text-sm">
            How would you like to receive your order?
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Pickup Option */}
          {restaurant?.isPickupModeEnabled == true && (
            <Card
              className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                selectedType === "pickup"
                  ? "border-emerald-500 bg-emerald-50 shadow-lg transform scale-105"
                  : "border-gray-200 hover:border-emerald-300"
              }`}
              onClick={() => setSelectedType("pickup")}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      selectedType === "pickup"
                        ? "bg-emerald-600 text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Store className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Pickup
                      </h3>
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        15-20 min
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Ready for pickup at restaurant
                    </p>
                    {/* <div className="flex items-center space-x-1 mt-2 text-green-600 text-sm font-medium">
                      <span>FREE</span>
                      <span>•</span>
                    <span>Faster</span>
                    </div> */}
                  </div>
                  {selectedType === "pickup" && (
                    <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Delivery Option */}
          {restaurant?.isDeliveryModeEnabled == true && (
            <Card
              className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                selectedType === "delivery"
                  ? "border-emerald-500 bg-emerald-50 shadow-lg transform scale-105"
                  : "border-gray-200 hover:border-emerald-300"
              }`}
              onClick={() => setSelectedType("delivery")}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      selectedType === "delivery"
                        ? "bg-emerald-600 text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Truck className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Delivery
                      </h3>
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        25-35 min
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Delivered to your location
                    </p>
                    <div className="flex items-center space-x-1 mt-2 text-gray-600 text-sm">
                      <MapPin className="w-3 h-3" />
                      {deliveryFee ? (
                        <span>${deliveryFee.toFixed(2)} delivery fee</span>
                      ) : (
                        <span className="text-gray-500 italic">
                          Delivery charges will be additional
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedType === "delivery" && (
                    <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          {/* <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              </div>
              <div className="text-blue-800 text-sm">
                <p className="font-medium mb-1">Good to know:</p>
                <ul className="space-y-0.5 text-xs">
                  <li>• Free delivery on orders over $35</li>
                  <li>• You can change this later if needed</li>
                </ul>
              </div>
            </div>
          </div> */}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 h-12">
            Cancel
          </Button>
          <Button onClick={handleConfirm} className="flex-1 h-12 btn-primary">
            <span>Continue with {selectedType}</span>
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
